import React, { useEffect, useRef } from 'react';
import { Title, useDataProvider } from 'react-admin';
import { Box, Grid } from '@mui/material';
import { formatDateTime } from '../../components/ex_dayjs';
import RenderControls from '../../components/ra-list/RenderControls';
import { DataGrid, GridColDef, GridRowParams, GridSortModel } from '@mui/x-data-grid';
import { REMOTE_API, objectToFormData } from './utils';
import SourceSelect from './components/SourceSelect';
import { loadResourceCSV, loadSourceType } from '../../utils/common';
import RuleEditPopup from './components/RuleEditPopup';
import TagsAutoComplete from './components/TagsAutoComplete';
import { FilterListProvider } from '../../context/FilterListProvider';
import NewRuleButton from './components/NewRuleButton';
import { ICsvMergeRow, ICsvMergeState } from './types';

/**
 * CSV Merge list page: source/tags filters, a grid of existing rules, and
 * the rule create/edit dialog (`RuleEditPopup`) opened via row click or the
 * "New Rule" button.
 */
const CsvMerge = () => {
    const dataProvider = useDataProvider();
    const refbtnFilter = useRef<HTMLButtonElement>(null);
    const [sortModel, setSortModel] = React.useState<GridSortModel>([{ field: 'Timestamp', sort: 'desc' }]);

    const [state, setState] = React.useState<ICsvMergeState>({
        sourceName: '',
        tags: [],
        isLoading: false,
        sources: [],
        rows: [],
        marketType: [],
        // for rule dialog
        isOpenRule: false,
        rowSelected: null, // row selected
        sourceVariable: []
    });

    useEffect(() => {
        getResource();
        loadSourceType(dataProvider, (response: any) => setState((prev) => ({ ...prev, marketType: response })));
        loadSourceVariable();
        refbtnFilter.current!.click();
    }, []);

    function getResource () {
        loadResourceCSV(dataProvider, (response: any) => setState((prev) => ({ ...prev, sources: response })));
    }

    function loadSourceVariable () {
        dataProvider.fetchData('SourceVariable', 'Filter')
            .then(function (response: any) {
                if (response && response.data)
                    setState((prev) => ({ ...prev, sourceVariable: response.data.items || [] }));
            }).catch((error: any) => console.error(error));
    }

    const handleSortModelChange = (newModel: GridSortModel) => {
        setSortModel(newModel);
    };

    const reloadCsv = (data: Record<string, any>) => {
        let _source = state.sourceName;
        // eslint-disable-next-line eqeqeq -- loose check intentionally matches both '' and undefined
        if (_source == '' || _source == undefined) {
            _source = '_';
        }
        // `_tags` keeps its original array type here (not joined) so the loose `==` comparison
        // below relies on Array.prototype.toString() the same way the pre-migration code did:
        // both `[]` and `['']` stringify to `''` and fall through to the `'_'` default.
        let _tags: string[] | string = state.tags;
        // eslint-disable-next-line eqeqeq -- intentionally loose: relies on Array.prototype.toString() coercion, see comment above
        if (_tags.length == 0 || _tags == undefined) {
            _tags = '_';
        }

        dataProvider.postData(REMOTE_API, 'Rule/by/source/' + _source + '/tags/' + _tags, objectToFormData(data))
            .then((response: any) => {
                if (response && response.data) {
                    let rows: ICsvMergeRow[] = [];
                    response.data.map((m: any) => {
                        let applyFor = m.ApplyFor.join(',');
                        rows.push({ ...m.RuleData, Source: applyFor, Tags: tagFormatter(m.RuleData.Tags) });
                    });
                    setState((prev) => ({ ...prev, rows, isLoading: false }));
                }
            }).catch((error: any) => console.error(error));
    };

    function tagFormatter (value: string) {
        if (value === undefined || value === ',,') {
            value = '';
            return value;
        }
        if (value.length === 0) return value;
        let result = value.replace(/^\,/, '').replace(/,$/, '');
        return result;
    }

    const handleSearch = (data: Record<string, any>) => {
        setState((prev) => ({ ...prev, isLoading: true }));
        reloadCsv(data);
    };

    const handleRowClick = (e: GridRowParams<ICsvMergeRow>) => {
        setState((prev) => ({ ...prev, isOpenRule: true, rowSelected: e.row, ruleId: e.id as number }));
    };

    const onNewRule = (filterList: Record<string, any>) => {
        setState((prev) => ({
            ...prev,
            isOpenRule: true,
            rowSelected: null,
            ruleId: [
                -1, {
                    // eslint-disable-next-line eqeqeq -- loose check intentionally matches both '' and '_'
                    sources: [prev.sourceName].filter((x) => x != '' && x != '_'),
                    tags: prev.tags,
                    name: filterList.name
                }
            ]
        }));
    };

    return (
        <Box>
            <SourceSelect label="Source"
                choices={state.sources}
                marketType={state.marketType}
                sourceVariable={state.sourceVariable}
                dataProvider={dataProvider}
                onSelect={(value) => {
                    setState((prev) => ({ ...prev, sourceName: value }));
                    setTimeout(() => refbtnFilter.current!.click());
                }}
                onDeleted={() => getResource()} />
            <Grid container size={3}>
                <TagsAutoComplete label={'Tags'} onChange={(e) => setState((prev) => ({ ...prev, tags: e as string[] }))} />
            </Grid>
            <Title title={'Csv Merge Filters'}></Title>
            <FilterListProvider>
                <RenderControls
                    controls={[
                        { component: 'text', name: 'name' },
                        { component: 'number', name: 'id' },
                        { component: 'text', name: 'createdby' },
                        { component: 'autocomplete', name: 'matches', choices: ['Event', 'Venue', 'EventDate', 'EventTime', 'Quantity', 'Section', 'Row', 'Cost', 'InHandDate', 'DeliveryMethod', 'TicketID', 'Notes'] },
                        { component: 'autocomplete', name: 'actions', choices: ['Remove', 'Event', 'Venue', 'EventDate', 'EventTime', 'Quantity', 'Section', 'Row', 'Cost', 'InHandDate', 'DeliveryMethod', 'TicketID', 'Notes'].map((m) => m === 'Remove' ? 'Remove' : 'Modify ' + m) }
                    ]}
                    refFilter={refbtnFilter}
                    disabled={state.isLoading}
                    onSearch={handleSearch}
                    actions={<NewRuleButton callback={onNewRule} />}
                />
            </FilterListProvider>
            <Box sx={{ height: 750, width: '100%' }}>
                <DataGrid
                    columns={[
                        { field: 'Id', headerName: 'Id', maxWidth: 100, flex: 0.3 },
                        { field: 'Name', headerName: 'Name', maxWidth: 600, flex: 1 },
                        { field: 'Source', headerName: 'Source', maxWidth: 600, flex: 1 },
                        { field: 'CreatedBy', headerName: 'Created by', maxWidth: 300, flex: 0.5 },
                        { field: 'Timestamp', headerName: 'Timestamp', maxWidth: 250, valueFormatter: (value) => formatDateTime(value), flex: 0.3 },
                        { field: 'Tags', headerName: 'Tags', maxWidth: 200, flex: 0.3 }
                    ] as GridColDef<ICsvMergeRow>[]}
                    rows={state.rows}
                    getRowId={(r) => r.Id}
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    onRowClick={handleRowClick}
                />
            </Box>
            {state.isOpenRule &&
                <RuleEditPopup
                    data={state}
                    dataProvider={dataProvider}
                    reloadPage={() => refbtnFilter.current!.click()}
                    onClose={() => setState((prev) => ({ ...prev, isOpenRule: false }))} />}
        </Box>
    );
};

const CsvMergePage = {
    name: 'CsvMerge',
    list: CsvMerge
};
export default CsvMergePage;